// External libraries
import { action } from 'easy-peasy';

// Internal libraries
import { getGlobals, getLocale } from '@/lib/globals';
import { $logger } from '@/lib/logger';

/**
 * CART MODEL
 * Data in this model is only set only once, when the
 * initial session data API call returns
 */
const getCartState = () => {
  const { checkoutSession } = getGlobals().initialGqlData;
  const { cart, merchant, creditMeta } = checkoutSession;
  const { currencyCode, currencyValue } = cart.amounts.total;
  const { country: merchantCountry, name: merchantName } = merchant;

  return {
    allowedCardTypes: [],
    cancelUrl: '',
    cartAmount: {
      currencyCode: currencyCode || '',
      currencyFormat: '',
      currencyFormatSymbolISOCurrency: '',
      currencySymbol: '',
      currencyValue: currencyValue || '',
    },
    cartDetails: {
      amounts: {},
      description: '',
      intent: '',
      items: [],
      merchant: {
        country: merchantCountry || '',
        name: merchantName || '',
      },
      noteToBuyer: '',
    },
    creditMeta: {
      upstreamCreditPresentment: creditMeta.upstreamCreditPresentment || false,
    },
    isShipToStore: false,
    shippingMethods: [],
  };
};

const createCartActions = () => ({
  setCartData: action((state, payload) => {
    Object.assign(state, payload);
  }),
});

/**
 * GRIFFIN METADATA MODEL
 * This model contains metadata generated by Griffin that
 * is fetched from the API. It is set after the initial Griffin
 * metadata API call returns as well as when
 */
const getGriffinState = () => {
  const INITIAL_LOCALE = getLocale().locale;
  const { localeMetadata } = getGlobals().initialGqlData;

  return {
    griffinMetadata: {
      address: {},
      currencyCode: localeMetadata.currencyCode,
      dateMeta: {
        datePattern: '',
        displayFormat: '',
      },
      phoneMeta: {
        mask: '',
        pattern: '',
      },
      territories: {
        [INITIAL_LOCALE]: [],
      },
      territoriesByRegion: {
        [INITIAL_LOCALE]: {
          AMERICAS: [],
          APAC: [],
          EMEA: [],
        },
      },
      territoriesToDialingCodeMap: [],
    },
  };
};

const createGriffinActions = () => ({
  // eslint-disable-next-line sort-keys
  addAddressMetadata: action((state, { key, payload }) => {
    if (!state) {
      $logger.warn('easy_peasy_state_missing', {
        action: 'addAddressMetadata',
      });
    }
    // `key` will be in the format of `country~lang`, e.g. 'US~en'
    state.griffinMetadata.address[key] = payload;
  }),
  setGriffinMetadata: action((state, payload) => {
    if (!state) {
      $logger.warn('easy_peasy_state_missing', {
        action: 'setGriffinMetadata',
      });
    }
    /**
     * Spreading the payload for the following use case:
     * User with locale en_US lands on page and changes their country to GB
     * Since we don't overwrite the address meta here if the users changes
     * their shipping address to the US we won't have to re-fetch that address
     * meta for the US.
     */
    state.griffinMetadata = {
      ...payload,
      address: {
        ...state.griffinMetadata.address,
        ...payload.address,
      },
      territories: {
        ...state.griffinMetadata.territories,
        ...payload.territories,
      },
      territoriesByRegion: {
        ...state.griffinMetadata.territoriesByRegion,
        ...payload.territoriesByRegion,
      },
    };
  }),
});

/**
 * MISC API MODEL
 * This model contains misc data returned from an API call
 */
const getMiscApiState = () => ({
  //Data pertaining to Bank Candidate data for Sepa flow
  bankCandidateData: {
    accountNumber: '',
    accountNumberShort: '',
    bankIdentifierCode: '',
    beneficiaries: [
      {
        fullName: '',
      },
    ],
    billingAddress: {
      city: '',
      fullAddress: '',
    },
    creditor: {
      address: {
        fullAddress: '',
      },
      id: '',
      name: '',
    },
    currencyCode: '',
    id: '',
    issuer: {
      branchLocation: '',
      city: '',
      country: '',
      name: '',
    },
    nickname: '',
    routingNumber: '',
    type: '',
  },

  installmentData: [],

  instrumentId: '',

  merchantId: '',

  // Data pertaining to Normalized Billing Address
  // eslint-disable-next-line sort-keys
  normalizedBillingAddress: {
    alreadyNormalized: false,
    city: '',
    griffinFormattedAddresses: {
      formattedNormalizedAddress: [],
      formattedOriginalAddress: [],
    },
    line1: '',
    line2: '',
    postalCode: '',
    state: '',
  },

  // Data pertaining to Normalized Shipping Address
  // eslint-disable-next-line sort-keys
  normalizedShippingAddress: {
    alreadyNormalized: false,
    city: '',
    griffinFormattedAddresses: {
      formattedNormalizedAddress: [],
      formattedOriginalAddress: [],
    },
    line1: '',
    line2: '',
    postalCode: '',
    state: '',
  },

  // Data used to help determine what address state should be
  // validated on form submission
  // eslint-disable-next-line sort-keys
  prefillAddressMetadata: {
    hasPrefillAddress: false,
    isPartialAddress: false,
  },

  shippingOptionsSource: '',

  // Data pertaining to 3DS (1.0)
  threeDS1ContingencyData: {},

  // Data pertaining to 3DS 2.0
  threeDS2ContingencyData: {},
});

const createMiscApiActions = () => ({
  setBankCandidateData: action((state, payload) => {
    state.bankCandidateData = payload;
  }),

  bankCurrencyConversionData: {}, // eslint-disable-line sort-keys
  setBankCurrencyConversionData: action((state, payload) => {
    state.bankCurrencyConversionData = payload;
  }),

  cupOtpEncryptedCardLoginAccountNumber: '', // eslint-disable-line sort-keys
  setCupOtpEncryptedCardLoginAccountNumber: action((state, payload) => {
    state.cupOtpEncryptedCardLoginAccountNumber = payload;
  }),

  cupOtpfundingInstrumentId: '', // eslint-disable-line sort-keys
  setCupOtpFundingInstrumentId: action((state, payload) => {
    state.cupOtpfundingInstrumentId = payload;
  }),

  currencyConversionData: {}, // eslint-disable-line sort-keys
  setCurrencyConversionData: action((state, payload) => {
    state.currencyConversionData = payload;
  }),

  setInstallmentData: action((state, payload) => {
    state.installmentData = payload;
  }),

  setInstrumentId: action((state, payload) => {
    state.instrumentId = payload;
  }),

  setMerchantId: action((state, payload) => {
    state.merchantId = payload;
  }),

  setNormalizedBillingAddress: action((state, payload) => {
    state.normalizedBillingAddress = payload;
  }),

  setNormalizedShippingAddress: action((state, payload) => {
    state.normalizedShippingAddress = payload;
  }),

  setPrefillAddressMetadata: action((state, payload) => {
    state.prefillAddressMetadata = {
      ...state.prefillAddressMetadata,
      ...payload,
    };
  }),

  setShippingOptionsSource: action((state, payload) => {
    state.shippingOptionsSource = payload;
  }),

  setThreeDS1ContingencyData: action((state, payload) => {
    state.threeDS1ContingencyData = payload;
  }),

  setThreeDS2ContingencyData: action((state, payload) => {
    state.threeDS2ContingencyData = payload;
  }),

  setUpstreamCreditPresentment: action((state, payload) => {
    state.creditMeta.upstreamCreditPresentment = payload;
  }),
});

const getApiState = overrides => ({
  ...getCartState(),
  ...getGriffinState(),
  ...getMiscApiState(),
  ...overrides,
});

const apiModelFactory = () => ({
  ...getApiState(),
  ...createCartActions(),
  ...createGriffinActions(),
  ...createMiscApiActions(),
});

export { getApiState, apiModelFactory };
